import { apiClient } from './client';
import type { AutomationStatus, AutomationTriggerKind } from './assist-automations';

/**
 * 회사 전체 업무자동화 — **보고 끄는** 자리 (설계 §07).
 *
 * ★★★ **`assist-automations.ts` 와 주소가 다르다.** 저쪽은 첫 줄부터 「내 것만」으로 서 있고
 *   (관리자도 남의 것은 안 보인다), 이쪽은 회사 것 전부를 본다. 한 파일에 역할 분기를 얹으면
 *   「내 것만」이라는 계약이 조건문 속으로 숨는다 — 서버도 같은 이유로 주소를 갈랐다.
 *
 * ★★ **할 수 있는 것은 켜기·끄기뿐이다.** 이름·묻는 말·언제 도는지는 만든 사람의 것이고,
 *   관리자에게 필요한 것은 「돌지 않게 하는 것」이다. 그래서 이 파일에 `create`·`delete` 가 없다.
 *
 * ★ **묻는 말(prompt)은 아예 안 온다** — 서버가 안 싣는다. 관리자가 볼 것은 「무엇이 돌고
 *   있나 · 누가 만들었나 · 잘 돌고 있나」이지 남이 무엇을 묻는지가 아니다.
 */

export interface CompanyAutomation {
  id: string;
  name: string;
  triggerKind: AutomationTriggerKind;
  status: AutomationStatus;
  /** 연속 실패 — 5가 되면 서버가 스스로 멈춘다. */
  failStreak: number;
  /** 왜 멈췄나. 관리자가 멈췄으면 「회사 관리자가 멈췄습니다.」가 들어 있다. */
  pausedReason: string | null;
  /** 관리자가 **잠갔나** — 잠긴 것은 만든 사람이 못 켠다. 판정은 서버의 `paused_at` 칸이 한다. */
  adminHeld: boolean;
  /** 누가 잠갔나. **모를 수 있다**(migration 190 이전 행) — 그때는 「회사 관리자」라고만 적는다. */
  pausedByName: string | null;
  pausedAt: string | null;
  lastRunAt: string | null;
  ownerUserId: string;
  /** 계정이 지워졌으면 「(지워진 계정)」 — **줄은 남는다**(감추면 돌고 있다는 사실까지 사라진다). */
  ownerName: string;
  /**
   * 만든 사람의 계정이 **살아 있나**.
   *
   * ★★★ 이것이 없으면 **퇴사자의 자동화가 「켜짐 · 마지막 실행 오늘 · 실패 0」으로 멀쩡해 보인다.**
   *   워커는 비활성 계정의 회차를 건너뛰는데, 건너뛴 회차도 `lastRunAt` 을 올리고 실패로 세지
   *   않기 때문이다 — 실제로는 한 번도 답을 준 적이 없다.
   */
  ownerActive: boolean;
}

export interface CompanyAutomationListResult {
  automations: CompanyAutomation[];
  /** 한 번에 주는 줄 수 — **서버가 준다.** 화면에 숫자를 또 적으면 언젠가 서로 달라진다. */
  limit: number;
  /**
   * 회사의 **전체** 자동화 수.
   *
   * ★★ 잘렸는지는 **서버만 안다.** 이것 없이 「줄 수 == 상한」으로 짐작하면, 정확히 상한만큼
   *   있는 회사에서 **없는 절단을 알리게** 된다.
   */
  total: number;
}

export async function listCompanyAutomations(): Promise<CompanyAutomationListResult> {
  const { data } = await apiClient.get<CompanyAutomationListResult>('/assist/automations/company');
  return data;
}

export async function setCompanyAutomationStatus(
  id: string, status: AutomationStatus,
): Promise<CompanyAutomation> {
  const { data } = await apiClient.patch<{ automation: CompanyAutomation }>(
    `/assist/automations/company/${id}`, { status },
  );
  return data.automation;
}
